// ============================================================================
// GOOGLE MAP COMPONENT v6.0 — CLEAN & SIMPLE UI
// ============================================================================
// IMPROVEMENTS:
// 1. Simple Ant Design loading spinner
// 2. No emojis - clean professional icons only
// 3. Simple export dropdown with text labels
// 4. Clean employee badges with numbers only
// 5. Professional tooltips without decorative symbols
// ============================================================================

import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Button, Dropdown, Menu, Space, message, Spin } from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FullscreenOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ProjectLocation } from '../../../services/polygonLocations.service';
import { CurrentCheckIn } from '../../../services/locationattendance.service';
import { Employee } from '../../../services/ptsemployees.service';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import EmployeeImageGallery from './EmployeeImageGallery';

// ============================================================================
// INTERFACES
// ============================================================================

interface GoogleMapComponentProps {
  projectLocations: ProjectLocation[];
  selectedProject: ProjectLocation | null;
  onProjectSelect: (project: ProjectLocation) => void;
  mapType: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
  currentCheckIns: CurrentCheckIn[];
  employees: Employee[];
  onMapLoad: () => void;
  searchedProject?: ProjectLocation | null;
}

interface SelectedLocationEmployees {
  location: google.maps.LatLngLiteral;
  employees: Array<{
    checkIn: CurrentCheckIn;
    employee: Employee | null;
  }>;
  projectName: string;
}

interface ValidatedCheckIn {
  checkIn: CurrentCheckIn;
  employee: Employee;
  projectLocation: ProjectLocation;
  isActive: boolean;
}

interface LocationGroup {
  locationKey: string;
  projectLocation: ProjectLocation;
  activeEmployees: ValidatedCheckIn[];
  checkedOutEmployees: ValidatedCheckIn[];
  totalCount: number;
}

// ============================================================================
// GOOGLE MAPS API READINESS HELPER
// ============================================================================

function waitForGoogleMaps(timeoutMs: number = 20000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.Map) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      if (window.google?.maps?.Map) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - startTime > timeoutMs) {
        clearInterval(interval);
        reject(new Error('Google Maps API timeout'));
      }
    }, 150);
  });
}

// ============================================================================
// DATE VALIDATION UTILITIES
// ============================================================================

const DateValidator = {
  _todayCache: null as string | null,
  _todayCacheTime: 0,

  getTodayDate(): string {
    const now = Date.now();
    if (this._todayCache && now - this._todayCacheTime < 60000) {
      return this._todayCache;
    }

    const date = new Date();
    const dubaiTime = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
    const year = dubaiTime.getFullYear();
    const month = String(dubaiTime.getMonth() + 1).padStart(2, '0');
    const day = String(dubaiTime.getDate()).padStart(2, '0');

    this._todayCache = `${year}-${month}-${day}`;
    this._todayCacheTime = now;

    return this._todayCache;
  },

  timestampToDate(timestamp: string | Date): string {
    try {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
      if (isNaN(date.getTime())) return '';

      const dubaiTime = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
      const year = dubaiTime.getFullYear();
      const month = String(dubaiTime.getMonth() + 1).padStart(2, '0');
      const day = String(dubaiTime.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  },

  formatTime(timestamp: string | Date): string {
    try {
      const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
      if (isNaN(date.getTime())) return '';

      const dubaiTime = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
      const hours = String(dubaiTime.getHours()).padStart(2, '0');
      const minutes = String(dubaiTime.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '';
    }
  },

  validateCheckIn(
    checkIn: CurrentCheckIn
  ): { isValid: boolean; reason: string; date: string } {
    if (!checkIn.checkInTime) {
      return { isValid: false, reason: 'No check-in time', date: '' };
    }

    const checkInDate = this.timestampToDate(checkIn.checkInTime);
    if (!checkInDate) {
      return { isValid: false, reason: 'Invalid timestamp', date: '' };
    }

    const today = this.getTodayDate();
    const isToday = checkInDate === today;

    return {
      isValid: isToday,
      reason: isToday ? 'Valid' : `Old date: ${checkInDate}`,
      date: checkInDate,
    };
  },
};

// ============================================================================
// EMPLOYEE MATCHER
// ============================================================================

const EmployeeMatcher = {
  _cache: new Map<string, string[]>(),

  getIdentifiers(id: string): string[] {
    if (this._cache.has(id)) {
      return this._cache.get(id)!;
    }

    const identifiers = new Set<string>();
    identifiers.add(id);
    identifiers.add(id.trim());
    identifiers.add(id.toLowerCase());
    identifiers.add(id.toUpperCase());

    const cleaned = id.replace(/^EMP/i, '').trim();
    identifiers.add(cleaned);
    identifiers.add(`EMP${cleaned}`);
    identifiers.add(cleaned.toLowerCase());
    identifiers.add(cleaned.toUpperCase());

    if (/^\d+$/.test(cleaned)) {
      identifiers.add(cleaned.padStart(4, '0'));
      identifiers.add(`EMP${cleaned.padStart(4, '0')}`);
    }

    const result = Array.from(identifiers);
    this._cache.set(id, result);
    return result;
  },

  findEmployee(employees: Employee[], searchId: string): Employee | null {
    const searchIdentifiers = this.getIdentifiers(searchId);

    for (const employee of employees) {
      const empIdentifiers = [...this.getIdentifiers(employee.id)];

      if (employee.pin) {
        empIdentifiers.push(...this.getIdentifiers(employee.pin));
      }

      if (employee.employeeNumber) {
        empIdentifiers.push(...this.getIdentifiers(employee.employeeNumber));
      }

      const match = searchIdentifiers.some((sid) =>
        empIdentifiers.some((eid) => eid.toLowerCase() === sid.toLowerCase())
      );

      if (match) return employee;
    }

    return null;
  },
};

// ============================================================================
// LOCATION MATCHER
// ============================================================================

const LocationMatcher = {
  createLookupMaps(
    projectLocations: ProjectLocation[]
  ): {
    byId: Map<string, ProjectLocation>;
    byName: Map<string, string>;
  } {
    const byId = new Map<string, ProjectLocation>();
    const byName = new Map<string, string>();

    projectLocations.forEach((location) => {
      byId.set(location.id, location);
      byName.set(location.name, location.id);
      byName.set(location.name.replace(/\s+/g, '-').toUpperCase(), location.id);
    });

    return { byId, byName };
  },

  findLocation(
    checkIn: CurrentCheckIn,
    byId: Map<string, ProjectLocation>,
    byName: Map<string, string>
  ): ProjectLocation | null {
    if (checkIn.locationId && byId.has(checkIn.locationId)) {
      return byId.get(checkIn.locationId)!;
    }

    if (checkIn.location) {
      if (byId.has(checkIn.location)) {
        return byId.get(checkIn.location)!;
      }

      if (byName.has(checkIn.location)) {
        const id = byName.get(checkIn.location)!;
        return byId.get(id)!;
      }
    }

    if (checkIn.checkInLocation) {
      if (byId.has(checkIn.checkInLocation)) {
        return byId.get(checkIn.checkInLocation)!;
      }

      if (byName.has(checkIn.checkInLocation)) {
        const id = byName.get(checkIn.checkInLocation)!;
        return byId.get(id)!;
      }
    }

    if (checkIn.locationLat && checkIn.locationLng) {
      return {
        id: checkIn.locationId || `temp-${checkIn.locationLat}-${checkIn.locationLng}`,
        name: checkIn.locationName || checkIn.location || 'Unknown Location',
        coordinates: {
          lat: checkIn.locationLat,
          lng: checkIn.locationLng,
        },
        polygon: [],
        status: 'active',
        type: 'construction',
        isActive: true,
        description: 'Location from check-in data',
        updatedAt: new Date().toISOString(),
      };
    }

    return null;
  },
};

// ============================================================================
// EXPORT UTILITIES
// ============================================================================

const ExportUtils = {
  exportToExcel(group: LocationGroup) {
    try {
      const allEmployees = [
        ...group.activeEmployees.map((item) => ({ ...item, status: 'Active' })),
        ...group.checkedOutEmployees.map((item) => ({ ...item, status: 'Checked Out' })),
      ];

      const exportData = allEmployees.map((item, index) => {
        const checkOutTime =
          (item.checkIn as any).checkOutTime ||
          (item.checkIn as any).checkout_time ||
          (item.checkIn as any).checkoutTime ||
          null;

        return {
          'No.': index + 1,
          'Employee ID': item.employee.id || '',
          'Employee Name': item.employee.name || '',
          Department: item.employee.department || '',
          Designation: item.employee.designation || '',
          Status: item.status,
          'Check-In Time': DateValidator.formatTime(item.checkIn.checkInTime),
          'Check-Out Time': checkOutTime ? DateValidator.formatTime(checkOutTime) : 'Not Checked Out',
          Location: group.projectLocation.name,
          Date: DateValidator.getTodayDate(),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = [
        { wch: 5 }, { wch: 12 }, { wch: 25 }, { wch: 20 }, { wch: 20 },
        { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 35 }, { wch: 12 },
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance');

      const fileName = `${group.projectLocation.name.replace(/[^a-z0-9]/gi, '_')}_Attendance_${DateValidator.getTodayDate()}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      message.success(`Exported ${allEmployees.length} employees to Excel`);
    } catch (error) {
      message.error('Failed to export to Excel');
      console.error('Export error:', error);
    }
  },

  exportToPDF(group: LocationGroup) {
    try {
      const pdf = new jsPDF();
      
      pdf.setFontSize(18);
      pdf.text('Phoenician Technical Services', 14, 20);
      pdf.setFontSize(14);
      pdf.text(`Location: ${group.projectLocation.name}`, 14, 28);
      pdf.setFontSize(10);
      pdf.text(`Date: ${DateValidator.getTodayDate()}`, 14, 34);
      pdf.text(`Total Employees: ${group.totalCount}`, 14, 40);

      const allEmployees = [
        ...group.activeEmployees.map((item) => ({ ...item, status: 'Active' })),
        ...group.checkedOutEmployees.map((item) => ({ ...item, status: 'Checked Out' })),
      ];

      const tableData = allEmployees.map((item, index) => {
        const checkOutTime =
          (item.checkIn as any).checkOutTime ||
          (item.checkIn as any).checkout_time ||
          (item.checkIn as any).checkoutTime ||
          null;

        return [
          index + 1,
          item.employee.name || '',
          item.employee.department || '',
          item.status,
          DateValidator.formatTime(item.checkIn.checkInTime),
          checkOutTime ? DateValidator.formatTime(checkOutTime) : 'Not Checked Out',
        ];
      });

      (pdf as any).autoTable({
        startY: 45,
        head: [['#', 'Employee', 'Department', 'Status', 'Check-In', 'Check-Out']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [24, 144, 255] },
        styles: { fontSize: 9 },
      });

      const fileName = `${group.projectLocation.name.replace(/[^a-z0-9]/gi, '_')}_Attendance_${DateValidator.getTodayDate()}.pdf`;
      pdf.save(fileName);
      
      message.success(`Exported ${allEmployees.length} employees to PDF`);
    } catch (error) {
      message.error('Failed to export to PDF');
      console.error('PDF export error:', error);
    }
  },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = React.memo((props) => {
  const {
    projectLocations,
    selectedProject,
    onProjectSelect,
    mapType,
    currentCheckIns,
    employees,
    onMapLoad,
    searchedProject,
  } = props;

  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const polygonsRef = useRef<Map<string, google.maps.Polygon>>(new Map());
  const employeeOverlaysRef = useRef<Map<string, google.maps.OverlayView>>(new Map());
  const hoverInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [selectedLocationEmployees, setSelectedLocationEmployees] =
    useState<SelectedLocationEmployees | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(11);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ========================================================================
  // VALIDATED DATA
  // ========================================================================

  const validatedCheckIns = useMemo(() => {
    if (currentCheckIns.length === 0) return [];
    return currentCheckIns.filter((checkIn) => {
      const validation = DateValidator.validateCheckIn(checkIn);
      return validation.isValid;
    });
  }, [currentCheckIns]);

  const matchedEmployees = useMemo(() => {
    if (validatedCheckIns.length === 0 || employees.length === 0) return [];

    const matched: Array<{ checkIn: CurrentCheckIn; employee: Employee }> = [];

    validatedCheckIns.forEach((checkIn) => {
      const employee = EmployeeMatcher.findEmployee(employees, checkIn.employeeId);
      if (employee) {
        matched.push({ checkIn, employee });
      }
    });

    return matched;
  }, [validatedCheckIns, employees]);

  const validatedData = useMemo(() => {
    if (matchedEmployees.length === 0) return [];

    const { byId, byName } = LocationMatcher.createLookupMaps(projectLocations);
    const validated: ValidatedCheckIn[] = [];

    matchedEmployees.forEach((item) => {
      const projectLocation = LocationMatcher.findLocation(item.checkIn, byId, byName);

      if (projectLocation) {
        const isActive =
          item.checkIn.isActive &&
          (!item.checkIn.workStatus ||
            ['In Progress', 'Active', 'Working'].includes(item.checkIn.workStatus));

        validated.push({
          checkIn: item.checkIn,
          employee: item.employee,
          projectLocation,
          isActive,
        });
      }
    });

    return validated;
  }, [matchedEmployees, projectLocations]);

  const locationGroups = useMemo(() => {
    if (validatedData.length === 0) return new Map<string, LocationGroup>();

    const groups = new Map<string, LocationGroup>();
    const seenEmployees = new Map<string, Set<string>>();

    validatedData.forEach((item) => {
      const locationKey = item.projectLocation.id;

      if (!groups.has(locationKey)) {
        groups.set(locationKey, {
          locationKey,
          projectLocation: item.projectLocation,
          activeEmployees: [],
          checkedOutEmployees: [],
          totalCount: 0,
        });
        seenEmployees.set(locationKey, new Set());
      }

      const group = groups.get(locationKey)!;
      const seen = seenEmployees.get(locationKey)!;

      if (!seen.has(item.employee.id)) {
        seen.add(item.employee.id);

        if (item.isActive) {
          group.activeEmployees.push(item);
        } else {
          group.checkedOutEmployees.push(item);
        }

        group.totalCount = group.activeEmployees.length + group.checkedOutEmployees.length;
      }
    });

    return groups;
  }, [validatedData]);

  // ========================================================================
  // PROJECT COLORS
  // ========================================================================

  const projectColors = useMemo(
    () => [
      '#38b2a8', '#f59e0b', '#8b5cf6', '#ef4444', '#10b981', '#3b82f6',
      '#f97316', '#84cc16', '#06b6d4', '#ec4899', '#6366f1', '#14b8a6',
    ],
    []
  );

  const getProjectColor = useCallback(
    (projectId: string): string => {
      const index = projectLocations.findIndex((p) => p.id === projectId);
      return projectColors[index % projectColors.length];
    },
    [projectLocations, projectColors]
  );

  const createMarkerIcon = useCallback(
    (project: ProjectLocation, isSelected: boolean = false): google.maps.Symbol => {
      const color = getProjectColor(project.id);
      const size = isSelected ? 12 : 8;
      const strokeWidth = isSelected ? 3 : 2;

      return {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: strokeWidth,
        scale: size,
        anchor: new google.maps.Point(0, 0),
      };
    },
    [getProjectColor]
  );

  // ========================================================================
  // CREATE HOVER INFO WINDOW
  // ========================================================================

  const createHoverInfoWindow = useCallback(() => {
    if (!hoverInfoWindowRef.current) {
      hoverInfoWindowRef.current = new google.maps.InfoWindow({
        disableAutoPan: true,
      });
    }
    return hoverInfoWindowRef.current;
  }, []);

  // ========================================================================
  // CREATE EMPLOYEE BADGE
  // ========================================================================

  const createDualEmployeeBadge = useCallback(
    (group: LocationGroup): google.maps.OverlayView | null => {
      if (!googleMapRef.current) return null;

      const activeCount = group.activeEmployees.length;
      const checkedOutCount = group.checkedOutEmployees.length;

      if (activeCount === 0 && checkedOutCount === 0) return null;

      const position = new google.maps.LatLng(
        group.projectLocation.coordinates.lat,
        group.projectLocation.coordinates.lng
      );

      class CompactDualBadgeOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private element: HTMLElement;

        constructor(
          position: google.maps.LatLng,
          group: LocationGroup,
          onBadgeClick: () => void,
          onExportExcel: () => void,
          onExportPDF: () => void,
          onHover: (show: boolean) => void
        ) {
          super();
          this.position = position;
          this.element = this.createBadgeElement(
            group,
            onBadgeClick,
            onExportExcel,
            onExportPDF,
            onHover
          );
        }

        createBadgeElement(
          group: LocationGroup,
          onBadgeClick: () => void,
          onExportExcel: () => void,
          onExportPDF: () => void,
          onHover: (show: boolean) => void
        ): HTMLElement {
          const getSize = (count: number) => {
            if (count === 1) return { width: 32, height: 32, fontSize: 13 };
            if (count <= 5) return { width: 38, height: 38, fontSize: 15 };
            if (count <= 10) return { width: 44, height: 44, fontSize: 17 };
            if (count <= 30) return { width: 48, height: 48, fontSize: 18 };
            if (count <= 50) return { width: 52, height: 52, fontSize: 19 };
            return { width: 56, height: 56, fontSize: 20 };
          };

          const container = document.createElement('div');
          container.style.cssText = `
            position: absolute;
            transform: translate(-50%, -100%);
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            cursor: pointer;
            z-index: 1000;
          `;

          const badgeContainer = document.createElement('div');
          badgeContainer.style.cssText = `
            display: flex;
            gap: 6px;
            align-items: center;
            filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.2));
          `;

          // Active employees badge
          if (group.activeEmployees.length > 0) {
            const size = getSize(group.activeEmployees.length);
            const badge = document.createElement('div');

            const isOverCapacity = group.activeEmployees.length >= 70;
            const bgColor = isOverCapacity
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)';

            badge.style.cssText = `
              position: relative;
              width: ${size.width}px;
              height: ${size.height}px;
              border-radius: 50%;
              background: ${bgColor};
              border: 3px solid white;
              box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.3s ease;
            `;

            badge.innerHTML = `
              <div style="color: white; font-size: ${size.fontSize}px; font-weight: 800; text-shadow: 0 2px 4px rgba(0,0,0,0.3); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui; line-height: 1;">${group.activeEmployees.length}</div>
            `;

            badge.addEventListener('mouseenter', () => {
              badge.style.transform = 'scale(1.15)';
              badge.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.35)';
            });
            badge.addEventListener('mouseleave', () => {
              badge.style.transform = 'scale(1)';
              badge.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.25)';
            });

            badgeContainer.appendChild(badge);
          }

          // Checked out employees badge
          if (group.checkedOutEmployees.length > 0) {
            const size = getSize(group.checkedOutEmployees.length);
            const badge = document.createElement('div');
            badge.style.cssText = `
              position: relative;
              width: ${size.width}px;
              height: ${size.height}px;
              border-radius: 50%;
              background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
              border: 3px solid white;
              box-shadow: 0 2px 12px rgba(107, 114, 128, 0.35);
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0.9;
              transition: all 0.3s ease;
            `;

            badge.innerHTML = `
              <div style="color: white; font-size: ${size.fontSize}px; font-weight: 800; text-shadow: 0 2px 4px rgba(0,0,0,0.3); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui; line-height: 1;">${group.checkedOutEmployees.length}</div>
            `;

            badge.addEventListener('mouseenter', () => {
              badge.style.transform = 'scale(1.15)';
              badge.style.opacity = '1';
            });
            badge.addEventListener('mouseleave', () => {
              badge.style.transform = 'scale(1)';
              badge.style.opacity = '0.9';
            });

            badgeContainer.appendChild(badge);
          }

          // Export button with dropdown
          const exportBtnContainer = document.createElement('div');
          exportBtnContainer.style.cssText = `
            position: absolute;
            top: -10px;
            right: -10px;
            z-index: 100;
          `;

          const exportBtn = document.createElement('button');
          exportBtn.innerHTML = `<svg viewBox="0 0 1024 1024" width="14" height="14" fill="currentColor"><path d="M505.7 661a8 8 0 0 0 12.6 0l112-141.7c4.1-5.2.4-12.9-6.3-12.9h-74.1V168c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8v338.3H400c-6.7 0-10.4 7.7-6.3 12.9l112 141.8z"/><path d="M878 626h-60c-4.4 0-8 3.6-8 8v154H214V634c0-4.4-3.6-8-8-8h-60c-4.4 0-8 3.6-8 8v198c0 17.7 14.3 32 32 32h684c17.7 0 32-14.3 32-32V634c0-4.4-3.6-8-8-8z"/></svg>`;
          exportBtn.title = 'Export Options';
          exportBtn.style.cssText = `
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            border: 2px solid white;
            color: white;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5);
            transition: all 0.3s ease;
          `;

          let menuVisible = false;
          const menu = document.createElement('div');
          menu.style.cssText = `
            position: absolute;
            top: 35px;
            right: 0;
            background: white;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
            padding: 4px;
            display: none;
            flex-direction: column;
            gap: 2px;
            min-width: 140px;
            z-index: 1000;
            border: 1px solid #e5e7eb;
          `;

          const createMenuItem = (text: string, onClick: () => void) => {
            const item = document.createElement('button');
            item.textContent = text;
            item.style.cssText = `
              padding: 8px 12px;
              border: none;
              background: transparent;
              cursor: pointer;
              text-align: left;
              font-size: 13px;
              border-radius: 6px;
              transition: background 0.2s ease;
              color: #1f2937;
              font-weight: 500;
              white-space: nowrap;
            `;
            item.addEventListener('mouseenter', () => {
              item.style.background = '#f3f4f6';
            });
            item.addEventListener('mouseleave', () => {
              item.style.background = 'transparent';
            });
            item.addEventListener('click', (e) => {
              e.stopPropagation();
              onClick();
              menu.style.display = 'none';
              menuVisible = false;
            });
            return item;
          };

          menu.appendChild(createMenuItem('Export Excel', onExportExcel));
          menu.appendChild(createMenuItem('Export PDF', onExportPDF));

          exportBtnContainer.appendChild(menu);

          exportBtn.addEventListener('mouseenter', () => {
            exportBtn.style.transform = 'scale(1.2)';
            exportBtn.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.6)';
          });

          exportBtn.addEventListener('mouseleave', () => {
            if (!menuVisible) {
              exportBtn.style.transform = 'scale(1)';
              exportBtn.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.5)';
            }
          });

          exportBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            menuVisible = !menuVisible;
            menu.style.display = menuVisible ? 'flex' : 'none';
            if (menuVisible) {
              exportBtn.style.transform = 'scale(1.2)';
            }
          });

          exportBtnContainer.appendChild(exportBtn);
          container.appendChild(badgeContainer);
          container.appendChild(exportBtnContainer);

          let hoverTimeout: ReturnType<typeof setTimeout> | null = null;
          let isHovering = false;

          container.addEventListener('mouseenter', () => {
            if (hoverTimeout) clearTimeout(hoverTimeout);
            isHovering = true;
            hoverTimeout = setTimeout(() => {
              if (isHovering) onHover(true);
            }, 400); // Match the delay in onHover function
          });

          container.addEventListener('mouseleave', () => {
            isHovering = false;
            if (hoverTimeout) clearTimeout(hoverTimeout);
            // Close immediately when mouse leaves
            onHover(false);
          });

          container.addEventListener('click', (e) => {
            if (e.target !== exportBtn && e.target !== menu && !menu.contains(e.target as Node)) {
              e.stopPropagation();
              onBadgeClick();
            }
          });

          document.addEventListener('click', (e) => {
            if (!container.contains(e.target as Node)) {
              menu.style.display = 'none';
              menuVisible = false;
            }
          });

          return container;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) panes.overlayMouseTarget.appendChild(this.element);
        }

        draw() {
          const projection = this.getProjection();
          if (projection) {
            const pos = projection.fromLatLngToDivPixel(this.position);
            if (pos) {
              this.element.style.left = pos.x + 'px';
              this.element.style.top = pos.y + 'px';
            }
          }
        }

        onRemove() {
          if (this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
          }
        }
      }

      const onBadgeClick = () => {
        setSelectedLocationEmployees({
          location: group.projectLocation.coordinates,
          employees: [
            ...group.activeEmployees.map((item) => ({
              checkIn: item.checkIn,
              employee: item.employee,
            })),
            ...group.checkedOutEmployees.map((item) => ({
              checkIn: item.checkIn,
              employee: item.employee,
            })),
          ],
          projectName: group.projectLocation.name,
        });
      };

      const onExportExcel = () => {
        ExportUtils.exportToExcel(group);
      };

      const onExportPDF = () => {
        ExportUtils.exportToPDF(group);
      };

      let hoverDelayTimeout: ReturnType<typeof setTimeout> | null = null;

      const onHover = (show: boolean) => {
        const infoWindow = createHoverInfoWindow();

        // Clear any pending timeout
        if (hoverDelayTimeout) {
          clearTimeout(hoverDelayTimeout);
          hoverDelayTimeout = null;
        }

        if (show && googleMapRef.current) {
          // Add delay before showing tooltip to prevent flickering
          hoverDelayTimeout = setTimeout(() => {
            const content = `
              <div style="padding: 14px; min-width: 220px; max-width: 320px; pointer-events: none;">
                <div style="font-weight: 700; font-size: 15px; margin-bottom: 10px; color: #1f2937;">
                  ${group.projectLocation.name}
                </div>
                ${
                  group.activeEmployees.length > 0
                    ? `
                  <div style="margin-bottom: 8px;">
                    <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
                      <span style="color: #10b981; font-weight: 700; font-size: 13px;">Active Employees</span>
                      <span style="background: #d1fae5; color: #065f46; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700;">${group.activeEmployees.length}</span>
                    </div>
                    <div style="font-size: 11px; color: #6b7280; max-height: 130px; overflow-y: auto;">
                      ${group.activeEmployees
                        .slice(0, 10)
                        .map(
                          (item) =>
                            `<div style="padding: 3px 0; display: flex; justify-content: space-between;">
                              <span>${item.employee.name}</span>
                              <span style="color: #10b981; font-weight: 600; margin-left: 8px;">${DateValidator.formatTime(item.checkIn.checkInTime)}</span>
                            </div>`
                        )
                        .join('')}
                      ${group.activeEmployees.length > 10 ? `<div style="padding: 3px 0; font-weight: 700;">...and ${group.activeEmployees.length - 10} more</div>` : ''}
                    </div>
                  </div>
                `
                    : ''
                }
                ${
                  group.checkedOutEmployees.length > 0
                    ? `
                  <div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb;">
                    <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
                      <span style="color: #6b7280; font-weight: 700; font-size: 13px;">Checked Out</span>
                      <span style="background: #e5e7eb; color: #374151; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700;">${group.checkedOutEmployees.length}</span>
                    </div>
                    <div style="font-size: 11px; color: #9ca3af; max-height: 130px; overflow-y: auto;">
                      ${group.checkedOutEmployees
                        .slice(0, 10)
                        .map((item) => {
                          const checkOutTime =
                            (item.checkIn as any).checkOutTime ||
                            (item.checkIn as any).checkout_time ||
                            (item.checkIn as any).checkoutTime ||
                            null;
                          return `<div style="padding: 3px 0; display: flex; justify-content: space-between;">
                            <span>${item.employee.name}</span>
                            <span style="color: #9ca3af; font-weight: 600; margin-left: 8px;">${checkOutTime ? DateValidator.formatTime(checkOutTime) : 'N/A'}</span>
                          </div>`;
                        })
                        .join('')}
                      ${group.checkedOutEmployees.length > 10 ? `<div style="padding: 3px 0; font-weight: 700;">...and ${group.checkedOutEmployees.length - 10} more</div>` : ''}
                    </div>
                  </div>
                `
                    : ''
                }
                <div style="margin-top: 12px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280;">
                  Click badge to view all employees
                </div>
              </div>
            `;

            infoWindow.setContent(content);
            // Position tooltip away from cursor to avoid interference
            const offsetPosition = new google.maps.LatLng(
              position.lat() + 0.003,
              position.lng() + 0.004
            );
            infoWindow.setPosition(offsetPosition);
            infoWindow.open(googleMapRef.current);
          }, 400); // 400ms delay to prevent flickering on quick mouse movements
        } else {
          // Close immediately when mouse leaves
          infoWindow.close();
        }
      };

      const overlay = new CompactDualBadgeOverlay(
        position,
        group,
        onBadgeClick,
        onExportExcel,
        onExportPDF,
        onHover
      );
      overlay.setMap(googleMapRef.current);

      return overlay;
    },
    [createHoverInfoWindow]
  );

  // ========================================================================
  // MAP INITIALIZATION
  // ========================================================================

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps?.Map) {
      return false;
    }

    if (googleMapRef.current) {
      return true;
    }

    const defaultCenter: google.maps.LatLngLiteral = { lat: 25.2048, lng: 55.2708 };

    const bounds = new google.maps.LatLngBounds();
    projectLocations.forEach((project: ProjectLocation) => {
      bounds.extend(project.coordinates);
    });

    const map = new google.maps.Map(mapRef.current, {
      zoom: 11,
      center: defaultCenter,
      mapTypeId: mapType === 'satellite' ? 'satellite' : 'roadmap',
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'cooperative',
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });

    googleMapRef.current = map;

    google.maps.event.addListenerOnce(map, 'idle', () => {
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
      }
      setTimeout(() => {
        setMapLoaded(true);
        onMapLoad();
      }, 300);
    });

    map.addListener('zoom_changed', () => {
      const zoom = map.getZoom();
      if (zoom) setCurrentZoom(zoom);
    });

    map.addListener('click', () => {
      setSelectedLocationEmployees(null);
      if (hoverInfoWindowRef.current) {
        hoverInfoWindowRef.current.close();
      }
    });

    return true;
  }, [projectLocations, mapType, onMapLoad]);

  // ========================================================================
  // EFFECT — WAIT FOR API, THEN INIT
  // ========================================================================

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        await waitForGoogleMaps(20000);

        if (cancelled) return;

        await new Promise(resolve => setTimeout(resolve, 200));

        if (cancelled) return;

        requestAnimationFrame(() => {
          if (!cancelled) {
            const success = initializeMap();
            if (!success) {
              setMapError('Map initialization failed. Please refresh the page.');
            }
          }
        });
      } catch (err) {
        if (!cancelled) {
          setMapError('Google Maps failed to load. Please check your connection.');
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current.clear();
      polygonsRef.current.forEach((polygon) => polygon.setMap(null));
      polygonsRef.current.clear();
      employeeOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
      employeeOverlaysRef.current.clear();
      if (hoverInfoWindowRef.current) {
        hoverInfoWindowRef.current.close();
      }
    };
  }, [initializeMap]);

  // ========================================================================
  // CREATE MAP ELEMENTS
  // ========================================================================

  const createMapElements = useCallback(() => {
    if (!googleMapRef.current || !mapLoaded) return;

    requestAnimationFrame(() => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current.clear();

      polygonsRef.current.forEach((polygon) => polygon.setMap(null));
      polygonsRef.current.clear();

      projectLocations.forEach((project) => {
        const isHighlighted =
          selectedProject?.id === project.id || searchedProject?.id === project.id;
        const marker = new google.maps.Marker({
          position: project.coordinates,
          map: googleMapRef.current,
          title: project.name,
          icon: createMarkerIcon(project, isHighlighted),
          optimized: true,
          zIndex: isHighlighted ? 1000 : 1,
          animation: isHighlighted ? google.maps.Animation.BOUNCE : undefined,
        });

        marker.addListener('mouseover', () => {
          const infoWindow = createHoverInfoWindow();
          infoWindow.setContent(`
            <div style="padding: 10px 14px; font-weight: 700; font-size: 14px; color: #1f2937;">
              ${project.name}
            </div>
          `);
          infoWindow.setPosition(project.coordinates);
          if (googleMapRef.current) {
            infoWindow.open(googleMapRef.current);
          }
        });

        marker.addListener('mouseout', () => {
          if (hoverInfoWindowRef.current) {
            hoverInfoWindowRef.current.close();
          }
        });

        marker.addListener('click', () => onProjectSelect(project));
        markersRef.current.set(project.id, marker);
      });

      projectLocations.forEach((project) => {
        if (!project.polygon || project.polygon.length < 3) return;

        const isHighlighted =
          selectedProject?.id === project.id || searchedProject?.id === project.id;
        const polygon = new google.maps.Polygon({
          paths: project.polygon,
          strokeColor: getProjectColor(project.id),
          strokeOpacity: isHighlighted ? 1.0 : 0.7,
          strokeWeight: isHighlighted ? 4 : 2,
          fillColor: getProjectColor(project.id),
          fillOpacity: isHighlighted ? 0.35 : 0.15,
          map: googleMapRef.current,
          clickable: true,
        });

        polygon.addListener('click', () => onProjectSelect(project));
        polygonsRef.current.set(project.id, polygon);
      });
    });
  }, [
    projectLocations,
    selectedProject,
    searchedProject,
    mapLoaded,
    onProjectSelect,
    createMarkerIcon,
    getProjectColor,
    createHoverInfoWindow,
  ]);

  const createEmployeeBadges = useCallback(() => {
    if (!googleMapRef.current || !mapLoaded) return;

    requestAnimationFrame(() => {
      employeeOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
      employeeOverlaysRef.current.clear();

      if (locationGroups.size === 0) return;

      locationGroups.forEach((group, locationKey) => {
        const overlay = createDualEmployeeBadge(group);
        if (overlay) employeeOverlaysRef.current.set(locationKey, overlay);
      });
    });
  }, [locationGroups, mapLoaded, createDualEmployeeBadge]);

  // ========================================================================
  // EFFECTS
  // ========================================================================

  useEffect(() => {
    if (mapLoaded) {
      createMapElements();
      createEmployeeBadges();
    }
  }, [mapLoaded, createMapElements, createEmployeeBadges]);

  useEffect(() => {
    if (googleMapRef.current && mapLoaded) {
      const actualMapType = mapType === 'satellite' ? 'satellite' : 'roadmap';
      googleMapRef.current.setMapTypeId(actualMapType);
    }
  }, [mapType, mapLoaded]);

  useEffect(() => {
    if (googleMapRef.current && selectedProject && mapLoaded) {
      googleMapRef.current.panTo(selectedProject.coordinates);
      const zoom = googleMapRef.current.getZoom();
      if (zoom !== null && zoom !== undefined && zoom < 15) {
        googleMapRef.current.setZoom(15);
      }
    }
  }, [selectedProject, mapLoaded]);

  // ========================================================================
  // MAP REFRESH HANDLER
  // ========================================================================

  const handleMapRefresh = useCallback(() => {
    if (!googleMapRef.current || !mapLoaded) {
      message.warning('Map not ready');
      return;
    }

    setIsRefreshing(true);
    message.loading({ content: 'Refreshing map...', key: 'refresh', duration: 0 });

    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current.clear();
    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    polygonsRef.current.clear();
    employeeOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
    employeeOverlaysRef.current.clear();

    const bounds = new google.maps.LatLngBounds();
    projectLocations.forEach((project) => {
      bounds.extend(project.coordinates);
    });

    if (!bounds.isEmpty()) {
      googleMapRef.current.fitBounds(bounds);
    }

    setTimeout(() => {
      createMapElements();
      createEmployeeBadges();
      
      setTimeout(() => {
        setIsRefreshing(false);
        message.success({ content: 'Map refreshed', key: 'refresh', duration: 2 });
      }, 500);
    }, 800);
  }, [mapLoaded, projectLocations, createMapElements, createEmployeeBadges]);

  // ========================================================================
  // MAP CONTROLS
  // ========================================================================

  const handleZoomIn = useCallback(() => {
    if (googleMapRef.current) {
      const currentZoom = googleMapRef.current.getZoom() || 11;
      googleMapRef.current.setZoom(currentZoom + 1);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (googleMapRef.current) {
      const currentZoom = googleMapRef.current.getZoom() || 11;
      googleMapRef.current.setZoom(currentZoom - 1);
    }
  }, []);

  const handleFullscreen = useCallback(() => {
    if (mapRef.current) {
      if (mapRef.current.requestFullscreen) {
        mapRef.current.requestFullscreen();
      }
    }
  }, []);

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#f3f4f6',
          minHeight: '400px',
          transition: 'opacity 0.3s ease',
          opacity: mapLoaded ? 1 : 0.5,
        }}
      />

      {mapLoaded && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            gap: 8,
            zIndex: 1000,
          }}
        >
          <Space>
            <Button
              icon={<ReloadOutlined spin={isRefreshing} />}
              onClick={handleMapRefresh}
              size="small"
              type="primary"
            >
              Refresh
            </Button>
            <Button.Group size="small">
              <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} />
              <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} />
              <Button icon={<FullscreenOutlined />} onClick={handleFullscreen} />
            </Button.Group>
          </Space>
        </div>
      )}

      {!mapLoaded && !mapError && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            borderRadius: 12,
          }}
        >
          <Spin size="large" tip="Loading map..." />
        </div>
      )}

      {mapError && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            padding: '32px',
            borderRadius: '16px',
            textAlign: 'center',
            zIndex: 2000,
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
            maxWidth: '400px',
          }}
        >
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626', marginBottom: 12 }}>
            Map Failed to Load
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: 24 }}>
            {mapError}
          </div>
          <Button
            type="primary"
            size="large"
            icon={<ReloadOutlined />}
            onClick={() => {
              setMapError(null);
              setMapLoaded(false);
              googleMapRef.current = null;
              waitForGoogleMaps(20000)
                .then(() => {
                  requestAnimationFrame(() => initializeMap());
                })
                .catch(() => {
                  setMapError('Google Maps is still unavailable.');
                });
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {selectedLocationEmployees && (
        <EmployeeImageGallery
          employees={selectedLocationEmployees.employees}
          projectName={selectedLocationEmployees.projectName}
          onClose={() => setSelectedLocationEmployees(null)}
        />
      )}
    </div>
  );
});

GoogleMapComponent.displayName = 'GoogleMapComponent';

export default GoogleMapComponent;